// FILE: clearConversation.ts
// Purpose: Confirmation + dispatch flow behind `/clear`, kept out of the view so the
//          "confirm before anything happens" rule is testable on its own.
// Layer: UI logic
// Depends on: orchestration command contracts only — deliberately no router, because
//             clearing must never move the user off the thread.
import type { ClientOrchestrationCommand, ThreadId } from "@taskin-code/contracts";

/**
 * Wording shown before a clear.
 *
 * Three facts, in the order that matters to someone about to press the button:
 * what disappears, that it cannot be undone, and what is explicitly kept. The
 * history survives in the event log, but the view never offers a way back to
 * it, so "hidden for good" is the honest description rather than "deleted".
 */
export const CLEAR_CONVERSATION_CONFIRMATION = [
  "Clear this conversation?",
  "The messages and activity shown here will be hidden for good — this cannot be undone.",
  "The agent starts fresh with no memory of the conversation. Your worktree, branch and files are kept, and the thread stays where it is.",
].join("\n");

export type ClearConversationOutcome = "unavailable" | "cancelled" | "cleared" | "failed";

export interface ClearConversationInput {
  readonly threadId: ThreadId | null | undefined;
  /** Clearing is an orchestration command; a local-only thread has nothing to clear. */
  readonly isServerThread: boolean;
  readonly confirm: (message: string) => Promise<boolean>;
  readonly dispatchCommand: (command: ClientOrchestrationCommand) => Promise<unknown>;
  readonly newCommandId: () => ClientOrchestrationCommand["commandId"];
  readonly now: () => string;
  readonly onUnavailable?: () => void;
  readonly onError?: (error: unknown) => void;
}

/**
 * Confirms first, then dispatches `thread.context.clear` for the current thread.
 *
 * Nothing is emitted when the user declines, and no navigation happens in any
 * branch: a clear leaves the user exactly where they were, on the same thread
 * and the same worktree.
 */
export async function requestClearConversation(
  input: ClearConversationInput,
): Promise<ClearConversationOutcome> {
  if (!input.isServerThread || !input.threadId) {
    input.onUnavailable?.();
    return "unavailable";
  }

  const confirmed = await input.confirm(CLEAR_CONVERSATION_CONFIRMATION);
  if (!confirmed) {
    return "cancelled";
  }

  try {
    await input.dispatchCommand({
      type: "thread.context.clear",
      commandId: input.newCommandId(),
      threadId: input.threadId,
      createdAt: input.now(),
    });
    return "cleared";
  } catch (error) {
    input.onError?.(error);
    return "failed";
  }
}
